import { isEmpty } from '@evertrue/sdk';
import CommentText from 'apps/interactions/components/lazy-comment-text';
import Decorator from 'clientDecorator';
import InteractionSubstantiveLabel from 'apps/interactions/components/interaction-substantive-label';
import HighlightText from 'components/formatting/highlight-text';
import InteractionUtils from 'apps/interactions/interaction-utils';
import { Link, Icon, Button } from '@evertrue/et-components';
import CommentMixin from 'mixins/comment-mixin';
import PropTypes from 'prop-types';
import ThankViewInteractionsDropdown from 'apps/interactions/components/interaction-thankview-dropdown';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import EverTrue from 'app';

/**
 * Interaction Component
 *
 * Displays interaction content with support for different interaction types,
 * highlighting, and various display options. Can optionally wrap content in a
 * clickable link for navigation and tracking.
 */
const Interaction = ({ flags, props, children }) => {
  const {
    interaction: {
      text = '',
      interaction_type = '',
      label = '',
      solicitor = {},
      summary = '',
      target_id = 0,
      mentions = [],
      pinned,
      substantive,
      secondary_target = [],
      contact = {},
      contact_id = 0,
      id,
    } = {},
    interaction,
    showSummary = true,
    showFullText,
    showContact = true,
    showMessage = true,
    highlight,
    contactId,
    className,
    limit,
    fromGlobalSearch = false,
  } = props;

  const is_gt_comment = interaction_type === 'EverTrue Comment';
  const is_tv_interaction = interaction_type === 'ThankView - Video';
  const tvMessage = is_tv_interaction ? interaction?.text.split('\r\n\r\n\r\n\r\n')[0] : '';
  const isEmptyContent = isEmpty(label) && isEmpty(solicitor) && isEmpty(summary) && isEmpty(text?.trim());
  const isSecondaryInteraction = !showContact && target_id !== contactId;
  const fullName = Decorator.Contacts.getFullName(contact);
  const secondaries = !!secondary_target.length;

  const handleClick = () => {
    const type = interaction_type === 'EverTrue Comment' ? 'comment' : 'interaction';
    EverTrue.track.set('interaction_action', { type, action: 'view' });
    EverTrue.Navigator(`/interaction/${id}`, { trigger: true });
  };

  const getTitle = () => {
    if (interaction_type === 'EverTrue Comment') {
      return 'Go to EverTrue Comment';
    }
    return summary ? `Go to interaction: ${summary}` : 'Go to interaction';
  };

  const content = (
    <div className={('interaction', className)}>
      <div className={`interaction--content ${!showFullText && 'is-snippet'}`}>
        <div className="interaction--main-content">
          <div
            className={`interaction--body ${is_gt_comment ? 'is-gt-comment' : ''} ${isEmptyContent ? 'is-empty' : ''}`}
          >
            {pinned && (
              <div className="interaction-pinned-tag">
                <Icon icon="pin-icon" />
                <span>&nbsp;Pinned to the profile</span>
              </div>
            )}
            <div className="interaction--header clearfix">
              <span className="interaction--details">
                {substantive && <InteractionSubstantiveLabel />}
                {fromGlobalSearch && <span className="interaction-meta--type fs-exclude-data">{interaction_type}</span>}
                {!isEmpty(contact) && showContact && fromGlobalSearch && (
                  <span>
                    <strong className="text-muted"> / </strong>
                    <Link className="fs-exclude-data" href={`/contact/${contact_id}`} title={`profile of ${fullName}`}>
                      {fullName}
                    </Link>
                    {!!secondaries && <span> +{secondaries}</span>}
                  </span>
                )}
                {isSecondaryInteraction && <span style={{ fontStyle: 'italic' }}> Non-primary</span>}
              </span>
              {children && children}
            </div>
            {showSummary && summary && (
              <div className="interaction--summary">
                <HighlightText
                  className="fs-exclude-data"
                  string={InteractionUtils.renderSummary(summary, flags.interactionDescriptionRichTextEditor)}
                  query={highlight}
                />
              </div>
            )}
            {showMessage && (
              <div className="interaction--text fs-exclude-data">
                {is_gt_comment ? (
                  <CommentText
                    text={InteractionUtils.renderText(
                      interaction,
                      showFullText,
                      limit,
                      flags.interactionDescriptionRichTextEditor
                    )}
                    highlight={highlight}
                    mentions={mentions}
                  />
                ) : is_tv_interaction && window.location.pathname === '/interaction' ? (
                  CommentMixin.getSnippet(tvMessage, 200)
                ) : is_tv_interaction ? (
                  <ThankViewInteractionsDropdown interaction={interaction} />
                ) : (
                  <HighlightText
                    string={InteractionUtils.renderText(
                      interaction,
                      showFullText,
                      limit,
                      flags.interactionDescriptionRichTextEditor
                    )}
                    query={highlight}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Button className="interaction-list-item" type="simple" title={getTitle()} onClick={handleClick}>
      {content}
    </Button>
  );
};

Interaction.propTypes = {
  props: PropTypes.object.isRequired,
  children: PropTypes.node,
  flags: PropTypes.object,
  interaction: PropTypes.object,
  showSummary: PropTypes.bool,
  showFullText: PropTypes.bool,
  showContact: PropTypes.bool,
  showMessage: PropTypes.bool,
  highlight: PropTypes.string,
  contactId: PropTypes.number,
  className: PropTypes.string,
  limit: PropTypes.number,
  fromGlobalSearch: PropTypes.bool,
};

export default withLDConsumer()(Interaction);
